import { Box, Card, Button, Text, Stack } from '@mantine/core'
import { IconClockExclamation } from '@tabler/icons-react'
import { BrandLogo } from '@/components/ui/BrandLogo'
import { APP_VERSION } from '@/version'

/**
 * Página estática de sessão expirada. Diferente do auto-redirect, ela NÃO
 * dispara o fluxo OIDC sozinha — o fluxo (e seus cookies temporizados de 30min/
 * 5min) só começa no clique, com o usuário presente. Mata o caso "aba esquecida
 * aberta → estado de login nasce vencido → 'sessão expirada'".
 *
 * Segue o padrão canônico das telas de auth (ForgotPassword.tsx): fundo `--ai-auth-bg`
 * com glow ambiente, BrandLogo, Card `--ai-auth-card-bg`, botão teal. Tokens em
 * docs/DESIGN_SYSTEM.md.
 */
export function SessionExpired() {
  const handleReenter = () => {
    const ret = sessionStorage.getItem('post_login_return_to') || '/'
    window.location.replace(
      '/api/v1/auth/oidc/start?return_to=' + encodeURIComponent(ret),
    )
  }

  return (
    <Box
      style={{
        minHeight: '100vh',
        background: 'var(--ai-auth-bg)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Subtle ambient glow */}
      <Box
        style={{
          position: 'absolute',
          top: '-30%',
          left: '50%',
          transform: 'translateX(-50%)',
          width: '600px',
          height: '600px',
          background:
            'radial-gradient(circle, rgba(9, 141, 147, 0.08) 0%, transparent 70%)',
          pointerEvents: 'none',
        }}
      />

      <Box style={{ width: '100%', maxWidth: 400, position: 'relative', zIndex: 1 }}>
        {/* Brand */}
        <Stack align="center" mb="xl" gap={4}>
          <BrandLogo variant="horizontal" color="white" width={180} />
          <Text
            size="xs"
            c="rgba(255,255,255,0.35)"
            mt={8}
            fw={600}
            tt="uppercase"
            style={{ letterSpacing: '0.08em' }}
          >
            Sessão Expirada
          </Text>
        </Stack>

        {/* Card */}
        <Card
          radius="md"
          p="xl"
          style={{
            backgroundColor: 'var(--ai-auth-card-bg)',
            border: '1px solid var(--ai-auth-border)',
            boxShadow: '0 16px 48px rgba(0, 0, 0, 0.3)',
          }}
        >
          <Stack align="center" gap="sm">
            <Box
              style={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(9, 141, 147, 0.1)',
                border: '1px solid rgba(9, 141, 147, 0.25)',
                boxShadow: '0 0 20px rgba(9, 141, 147, 0.15)',
              }}
            >
              <IconClockExclamation size={30} color="#098d93" stroke={1.6} />
            </Box>
            <Text fw={700} size="lg" c="#e9ecef" ta="center">
              Sua sessão expirou
            </Text>
            <Text size="sm" c="#b6c2c9" ta="center" lh={1.5}>
              Por segurança, sua sessão foi encerrada após um período de
              inatividade. Entre novamente para continuar de onde parou.
            </Text>
          </Stack>

          <Button
            fullWidth
            mt="xl"
            size="md"
            radius="sm"
            onClick={handleReenter}
            style={{
              backgroundColor: 'var(--ai-brand-verde)',
              border: 'none',
              fontWeight: 700,
              fontSize: 13,
              letterSpacing: '0.02em',
              transition: 'all var(--ai-transition-base)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#077a80'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--ai-brand-verde)'
            }}
          >
            Entrar novamente
          </Button>

          <Text size="xs" c="#51585e" ta="center" mt="xl" fw={600}>
            ConsoleAI v{APP_VERSION}
          </Text>
        </Card>

        <Text size="xs" c="rgba(255,255,255,0.2)" ta="center" mt="md" fw={500}>
          &copy; 2025-2026 Amazonas Inovare. All rights reserved.
        </Text>
      </Box>
    </Box>
  )
}
